/**
 * Caché global singleton para toda la app.
 * Permite guardar y recuperar cualquier recurso por clave.
 * TTL global de 5 minutos.
 * Se vacía en cada cambio de token (login, logout, cambio de team, impersonación)
 * para que una identidad nunca reutilice catálogos de otra.
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class GlobalCache {
  constructor() {}
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pending: Map<string, Promise<any>> = new Map(); // promesas en curso
  // Se incrementa en cada clear(): identifica lecturas iniciadas antes de un cambio de sesión.
  private generation = 0;

  /** Obtiene un valor del caché si no expiró */
  get<T>(key: string): T | undefined {
    this.cleanExpired();
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      return entry.data as T;
    }
    return undefined;
  }

  /** Generación actual; pásala a `set` cuando escribas tras un `await`. */
  getGeneration(): number {
    return this.generation;
  }

  /**
   * Guarda un valor en el caché. Con `generation`, descarta la escritura si la
   * caché se vació desde entonces (el dato pertenece a otra sesión).
   */
  set<T>(key: string, value: T, generation?: number): void {
    if (generation !== undefined && generation !== this.generation) {
      return;
    }
    this.cache.set(key, { data: value, timestamp: Date.now() });
  }

  /**
   * Obtiene un valor del caché o lo obtiene con fetcher y lo guarda.
   * @param key Clave única
   * @param fetcher Función que retorna una promesa del dato
   */
  async getOrSet<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== undefined) {
      return cached;
    }
    const generation = this.generation;
    const data = await fetcher();
    if (generation !== this.generation) {
      throw new DOMException('The authorization context changed.', 'AbortError');
    }
    this.set(key, data, generation);
    return data;
  }

  /** Limpia el caché completamente */
  clear() {
    this.generation++;
    this.cache.clear();
  }

  /** Limpia solo los valores expirados */
  cleanExpired() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }
}

// Exportar una única instancia global
const globalCache = new GlobalCache();

export default globalCache;
